/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * Component rendering coach CTA on the SAT landing page.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";

type Props = {
    educatorDescription: string;
    educatorTargetUrl: string;
    parentDescription: string;
    parentTargetUrl: string;
};

export default function CoachModule({
    educatorDescription,
    educatorTargetUrl,
    parentDescription,
    parentTargetUrl,
}: Props): React.ReactElement {
    const educatorCta = (
        <Button kind="secondary" size="large" href={educatorTargetUrl}>
            {t`Educators, learn more`}
        </Button>
    );
    const parentCta = (
        <Button kind="secondary" size="large" href={parentTargetUrl}>
            {t`Parents, learn more`}
        </Button>
    );

    return (
        <MediaLayout styleSheets={styleSheets}>
            {({styles, mediaSize}) => {
                return (
                    <View style={styles.coachWrapper}>
                        <View style={styles.coachContent}>
                            <View
                                style={[styles.coachBlock, styles.firstBlock]}
                            >
                                <h1
                                    // This is a <h1> instead of a Wonder Blocks typography
                                    // component because the font size does not conform to
                                    // Wonder Blocks specifications, since we are aligning
                                    // the style of this page with College Board's branding.
                                    // @ts-expect-error - TS2345 - Argument of type 'StyleType' is not assignable to parameter of type 'CSSProperties | Falsy'.
                                    className={css(styles.title)}
                                >
                                    {t`For educators`}
                                </h1>
                                <Body style={styles.bodyText}>
                                    {educatorDescription}
                                </Body>
                                <View style={styles.buttonContainer}>
                                    {educatorCta}
                                </View>
                            </View>

                            <View style={styles.coachBlock}>
                                {/* @ts-expect-error - TS2345 - Argument of type 'StyleType' is not assignable to parameter of type 'CSSProperties | Falsy'. */}
                                <h1 className={css(styles.title)}>
                                    {t`For parents`}
                                </h1>
                                <Body style={styles.bodyText}>
                                    {parentDescription}
                                </Body>
                                <View style={styles.buttonContainer}>
                                    {parentCta}
                                </View>
                            </View>
                        </View>
                    </View>
                );
            }}
        </MediaLayout>
    );
}

const styleSheets = {
    all: StyleSheet.create({
        bodyText: {
            color: color.offBlack,
            fontSize: 18,
            lineHeight: "30px",
            maxWidth: 450,
        },
        buttonContainer: {
            flexDirection: "row",
            marginTop: 40,
        },
        coachWrapper: {
            display: "flex",
            paddingLeft: 24,
            paddingRight: 24,
        },
        coachContent: {
            margin: "0 auto",
        },
        title: {
            color: "black",
            fontFamily: "Source Serif Pro",
            fontWeight: 600,
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale",
            marginBottom: 32,
        },
    }),

    mdOrLarger: StyleSheet.create({
        coachWrapper: {
            paddingTop: 112,
            paddingBottom: 112,
        },
        coachContent: {
            flexDirection: "row",
            justifyContent: "center",
        },
        coachBlock: {
            maxWidth: 448,
        },
        firstBlock: {
            marginRight: 120,
        },
        title: {
            fontSize: 44,
        },
    }),

    small: StyleSheet.create({
        // Note(nisha): The following styles are used. The linter is confused
        // on the last style so we have to disable eslint.
        // eslint-disable-next-line react-native/no-unused-styles
        coachWrapper: {
            paddingTop: 56,
            paddingBottom: 64,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        coachContent: {
            flexDirection: "column",
            maxWidth: 312,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        firstBlock: {
            marginBottom: 60,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        title: {
            fontSize: 30,
        },
    }),
} as const;
